import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { useSeo, SITE_URL, ORGANIZATION } from "@/lib/seo";
import { PRESS_URL, ABOUT_INTRO } from "@/lib/about";

const crumbs = [{ to: "/", label: "Accueil" }, { to: "/presse", label: "Espace presse" }];
const FACTS = [
  ["Création", "2011, par Pascal Chaubo"],
  ["Spécialité", "Chute de cheveux et cheveux fragilisés chez la femme afro, cheveux crépus et naturels, locks, barbe, enfants"],
  ["Fabrication", "Soins formulés et fabriqués en France"],
  ["Avis clients", "1 170 avis vérifiés, note moyenne 4,9/5 (Shopping Satisfaction)"],
  ["Livraison", "France, DOM-TOM, Europe, Afrique, Amérique du Nord, Royaume-Uni et Caraïbes"],
  ["Soin phare", "Extrême Repouss, lotion pour le cuir chevelu"],
];
const PHOTOS = [
  ["/editorial/coffret-vrais-produits-ozentya.webp", "Coffret de soins Ozentya"],
  ["/editorial/soins-locks.webp", "Gamme locks Ozentya"],
  ["/editorial/soin-barbe-afro.webp", "Gamme barbe Ozentya"],
];

export default function Presse() {
  useSeo({
    title: "Espace presse Ozentya : dossier, chiffres clés, photos | Ozentya",
    description: "Dossier de presse Ozentya : marque familiale antillaise de soins pour cheveux afro fondée en 2011 par Pascal Chaubo, chiffres clés, photos produits et contact presse.",
    path: "/presse",
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "WebPage", name: "Espace presse Ozentya", url: `${SITE_URL}/presse`, about: { "@id": ORGANIZATION["@id"] } },
      breadcrumbLd(SITE_URL, crumbs),
    ] },
  });
  return (
    <main data-testid="press-page" className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb items={crumbs} />
      <p className="eyebrow mb-6">Espace presse · journalistes, blogs, partenaires</p>
      <h1 className="max-w-5xl font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Ozentya en bref : dossier de presse</h1>
      <p className="mt-8 max-w-3xl text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">{ABOUT_INTRO}</p>
      <dl data-testid="press-facts" className="mt-14 grid max-w-5xl gap-px border border-[var(--line)] bg-[var(--line)] sm:grid-cols-2">
        {FACTS.map(([k, v]) => (
          <div key={k} className="bg-[var(--ink)] p-5">
            <dt className="text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)]">{k}</dt>
            <dd className="mt-2 text-sm leading-relaxed text-[var(--parchment)]">{v}</dd>
          </div>
        ))}
      </dl>
      <section className="mt-16 max-w-3xl">
        <h2 className="font-display text-3xl">Ils parlent d'Ozentya</h2>
        <a href={PRESS_URL} target="_blank" rel="noopener" data-testid="press-article-link" className="mt-4 inline-flex items-center gap-2 text-[var(--gold-soft)] underline underline-offset-4">« Le pari Ozentya », France-Antilles <ArrowUpRight className="h-4 w-4" /></a>
      </section>
      <section className="mt-16">
        <h2 className="font-display text-3xl">Photos libres de droits pour la presse</h2>
        <p className="mt-3 text-sm text-[var(--parchment)]/80">Utilisation éditoriale avec la mention « © Ozentya ». Clic droit puis « Enregistrer l'image » pour télécharger.</p>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {PHOTOS.map(([src, alt]) => (
            <a key={src} href={src} download data-testid={`press-photo-${src.split("/").pop().split(".")[0]}`} className="block overflow-hidden border border-[var(--line)]">
              <img src={src} alt={alt} loading="lazy" className="aspect-[4/5] w-full object-cover" />
              <span className="block p-3 text-xs text-[var(--parchment)]/85">{alt}</span>
            </a>
          ))}
        </div>
      </section>
      <section className="mt-16 max-w-3xl">
        <h2 className="font-display text-3xl">Contact presse</h2>
        <p className="mt-4 text-sm leading-relaxed text-[var(--parchment)]/85 md:text-base">Interview de Pascal Chaubo, informations sur la marque, visuels haute définition : écrivez-nous depuis la page <Link to="/contact" data-testid="press-contact-link" className="text-[var(--gold-soft)] underline underline-offset-4">contact</Link> en précisant votre média. Pour en savoir plus sur la marque : <Link to="/a-propos" className="text-[var(--gold-soft)] underline underline-offset-4">notre histoire</Link>.</p>
      </section>
    </main>
  );
}
